import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, formatDate, scr } from '../../lib/api';
import { sha256Hex } from '../../lib/hash';
import { useAuth } from '../../lib/auth';
import type { ClashRecord, WorksPermit, WorksPermitDocument } from '../../lib/types';
import { useApiQuery, useCursorList } from '../../lib/useApi';
import { BackLink, EmptyState, ErrorNote, Field, LoadMoreButton, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

const PERMIT_TYPES = ['ROAD_OPENING', 'HOARDING', 'SCAFFOLD', 'CRANE'] as const;
const DOC_TYPES = ['TRAFFIC_MGMT_PLAN', 'INSURANCE_CERT', 'SITE_PLAN', 'METHOD_STATEMENT', 'OTHER'] as const;
const LIST_STATES = ['SUBMITTED', 'APPROVED', 'ACTIVE', 'REINSTATEMENT_REVIEW', 'REWORK_REQUIRED', 'CLOSED', 'REJECTED', 'REVOKED'] as const;

export function WorksPermits() {
  const { persona } = useAuth();
  const isStaff = (persona?.roles ?? []).some((r) => ['WORKS_OFFICER', 'ROADS_ENGINEER'].includes(r));
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [showNew, setShowNew] = useState(false);

  const basePath = useMemo(() => {
    const params = new URLSearchParams();
    params.set('limit', '25');
    if (status) params.set('status', status);
    if (q.trim()) params.set('q', q.trim());
    return `/api/v1/m20/permits?${params.toString()}`;
  }, [status, q]);
  const list = useCursorList<WorksPermit>(isStaff ? basePath : null);

  return (
    <div>
      <PageHeader
        title="Works Permits"
        subtitle="Road opening, hoarding, scaffold, crane — clash-checked against the dig-once register at submit time."
        actions={<button className="btn-primary" onClick={() => setShowNew(true)}>+ Apply for permit</button>}
      />

      {!isStaff ? <div className="mb-4"><ErrorNote message="The staff queue (GET /permits) requires WORKS_OFFICER or ROADS_ENGINEER — you can still submit a new permit application below." /></div> : null}

      {isStaff ? (
        <>
          <div className="card mb-4 flex flex-wrap items-end gap-3 p-4">
            <Field label="Status" className="w-52">
              <select className="field-input" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">All statuses</option>
                {LIST_STATES.map((s) => <option key={s} value={s}>{s.replaceAll('_', ' ')}</option>)}
              </select>
            </Field>
            <Field label="Search" className="min-w-[200px] flex-1">
              <input className="field-input" placeholder="Ref no, corridor, applicant" value={q} onChange={(e) => setQ(e.target.value)} />
            </Field>
          </div>

          {list.error ? <div className="mb-4"><ErrorNote message={list.error} /></div> : null}

          <div className="card overflow-x-auto">
            <table className="w-full min-w-[720px] border-collapse">
              <thead>
                <tr>
                  <th className="table-head">Ref no</th>
                  <th className="table-head">Type</th>
                  <th className="table-head">Corridor</th>
                  <th className="table-head">District</th>
                  <th className="table-head">Clash</th>
                  <th className="table-head">State</th>
                  <th className="table-head">Dates</th>
                </tr>
              </thead>
              <tbody>
                {list.items.map((p) => (
                  <tr key={p.id} className="table-row-link" onClick={() => navigate(`/infrastructure/permits/${p.id}`)}>
                    <td className="table-cell font-semibold text-slate-800">{p.refNo}</td>
                    <td className="table-cell text-xs">{p.type.replaceAll('_', ' ')}</td>
                    <td className="table-cell">{p.corridor}</td>
                    <td className="table-cell">{p.district}</td>
                    <td className="table-cell">{p.hasClash ? <span className="text-xs font-semibold text-amber-600">Clash</span> : <span className="text-xs text-slate-400">Clear</span>}</td>
                    <td className="table-cell"><StatusBadge status={p.state} /></td>
                    <td className="table-cell text-xs text-slate-500">{formatDate(p.startDate)} – {formatDate(p.endDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {list.loading ? <Spinner label="Loading permits…" /> : null}
            {!list.loading && list.items.length === 0 ? <EmptyState title="No works permits found" /> : null}
          </div>
          <LoadMoreButton nextCursor={list.nextCursor} loading={list.loading} onClick={list.loadMore} />
        </>
      ) : null}

      {showNew ? (
        <NewPermitModal onClose={() => setShowNew(false)} onCreated={(p) => { setShowNew(false); navigate(`/infrastructure/permits/${p.id}`); }} />
      ) : null}
    </div>
  );
}

function NewPermitModal({ onClose, onCreated }: { onClose: () => void; onCreated: (p: WorksPermit) => void }) {
  const [type, setType] = useState<(typeof PERMIT_TYPES)[number]>('ROAD_OPENING');
  const [district, setDistrict] = useState('');
  const [corridor, setCorridor] = useState('');
  const [locationDescription, setLocationDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [insurerName, setInsurerName] = useState('');
  const [insurancePolicyNo, setInsurancePolicyNo] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clashes, setClashes] = useState<ClashRecord[] | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    setClashes(null);
    try {
      const permit = await api.post<WorksPermit>('/api/v1/m20/permits', {
        type,
        district,
        corridor,
        locationDescription,
        startDate,
        endDate,
        insurerName: insurerName || undefined,
        insurancePolicyNo: insurancePolicyNo || undefined,
        documents: [],
      });
      if (permit.clashes && permit.clashes.length > 0) {
        setClashes(permit.clashes);
        setTimeout(() => onCreated(permit), 1800);
      } else {
        onCreated(permit);
      }
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not submit permit application');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Apply for works permit" onClose={onClose} wide>
      <p className="mb-3 text-xs text-slate-500">
        POST /api/v1/m20/permits — clash-checked against the dig-once register and other in-flight permits on the
        same normalised corridor + district for any date overlap.
      </p>
      {error ? <div className="mb-3"><ErrorNote message={error} /></div> : null}
      {clashes ? (
        <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
          {clashes.length} corridor clash(es) detected — a Roads Engineer must supply a clashOverrideNote to approve. Opening the permit…
        </div>
      ) : null}
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Permit type">
          <select className="field-input" value={type} onChange={(e) => setType(e.target.value as typeof type)}>
            {PERMIT_TYPES.map((t) => <option key={t} value={t}>{t.replaceAll('_', ' ')}</option>)}
          </select>
        </Field>
        <Field label="District">
          <input className="field-input" value={district} onChange={(e) => setDistrict(e.target.value)} placeholder="e.g. Beau Vallon" />
        </Field>
        <Field label="Corridor" className="sm:col-span-2">
          <input className="field-input" value={corridor} onChange={(e) => setCorridor(e.target.value)} placeholder="e.g. Bel Ombre Road" />
        </Field>
        <Field label="Location description" className="sm:col-span-2">
          <input className="field-input" value={locationDescription} onChange={(e) => setLocationDescription(e.target.value)} />
        </Field>
        <Field label="Start date">
          <input className="field-input" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </Field>
        <Field label="End date">
          <input className="field-input" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </Field>
        <Field label="Insurer name (required for most types)">
          <input className="field-input" value={insurerName} onChange={(e) => setInsurerName(e.target.value)} />
        </Field>
        <Field label="Insurance policy no.">
          <input className="field-input" value={insurancePolicyNo} onChange={(e) => setInsurancePolicyNo(e.target.value)} />
        </Field>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={busy || !district || !corridor || !locationDescription || !startDate || !endDate} onClick={submit}>
          {busy ? 'Submitting…' : 'Submit application'}
        </button>
      </div>
    </Modal>
  );
}

export function WorksPermitDetail() {
  const { id = '' } = useParams();
  const { persona } = useAuth();
  const roles = persona?.roles ?? [];
  const isEngineer = roles.includes('ROADS_ENGINEER');
  const permitQ = useApiQuery<WorksPermit>(`/api/v1/m20/permits/${id}`);
  const docsQ = useApiQuery<{ items: WorksPermitDocument[] }>(`/api/v1/m20/permits/${id}/documents`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [clashOverride, setClashOverride] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [revokeReason, setRevokeReason] = useState('');
  const [docType, setDocType] = useState<(typeof DOC_TYPES)[number]>('SITE_PLAN');
  const [docRef, setDocRef] = useState('');
  const [reinstatementNotes, setReinstatementNotes] = useState('');
  const [inspectResult, setInspectResult] = useState<'PASS' | 'FAIL'>('PASS');

  const act = async (fn: () => Promise<unknown>, message?: string) => {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      await fn();
      permitQ.reload();
      if (message) setNote(message);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  if (permitQ.loading) return <Spinner label="Loading permit…" />;
  if (permitQ.error || !permitQ.data) return <ErrorNote message={permitQ.error ?? 'Permit not found'} />;
  const p = permitQ.data;

  return (
    <div>
      <BackLink to="/infrastructure/permits" />
      <PageHeader title={p.refNo} subtitle={`${p.type.replaceAll('_', ' ')} · ${p.corridor}, ${p.district}`} actions={<StatusBadge status={p.state} />} />
      {error ? <div className="mb-4"><ErrorNote message={error} /></div> : null}
      {note ? <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-700">{note}</div> : null}

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <div className="card p-5">
            <h2 className="mb-3 text-sm font-bold text-slate-700">Details</h2>
            <dl className="space-y-1.5 text-sm">
              <div className="flex justify-between"><dt className="text-slate-500">Location</dt><dd className="font-medium text-slate-800">{p.locationDescription}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Dates</dt><dd className="font-medium text-slate-800">{formatDate(p.startDate)} – {formatDate(p.endDate)}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Applicant</dt><dd className="font-medium text-slate-800">{p.applicantId} ({p.applicantType})</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Insurance</dt><dd className="font-medium text-slate-800">{p.insurerName ?? '—'} {p.insurancePolicyNo ? `(${p.insurancePolicyNo})` : ''}</dd></div>
            </dl>
            {p.hasClash ? (
              <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                <div className="mb-1 text-xs font-bold uppercase tracking-wide text-amber-700">Corridor clash detected</div>
                <ul className="space-y-1 text-xs text-amber-700">
                  {(p.clashesJson ?? []).map((c, i) => (
                    <li key={i}>{c.source} {c.refNo} — {c.status} ({formatDate(c.plannedStartDate)} – {formatDate(c.plannedEndDate)})</li>
                  ))}
                </ul>
                {p.clashOverrideNote ? <p className="mt-1 text-xs italic text-amber-700">Override: {p.clashOverrideNote}</p> : null}
              </div>
            ) : null}
          </div>

          <div className="card p-5">
            <h2 className="mb-3 text-sm font-bold text-slate-700">Supporting documents</h2>
            {docsQ.data?.items.length === 0 ? <EmptyState title="No documents attached" /> : null}
            <ul className="mb-3 space-y-1 text-xs text-slate-600">
              {docsQ.data?.items.map((d) => (
                <li key={d.id} className="flex justify-between border-b border-slate-100 py-1">
                  <span>{d.docType.replaceAll('_', ' ')}</span>
                  <span className="font-mono text-slate-400">{d.sha256.slice(0, 12)}…</span>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap items-end gap-2 border-t border-slate-100 pt-3">
              <Field label="Doc type" className="w-48">
                <select className="field-input" value={docType} onChange={(e) => setDocType(e.target.value as typeof docType)}>
                  {DOC_TYPES.map((t) => <option key={t} value={t}>{t.replaceAll('_', ' ')}</option>)}
                </select>
              </Field>
              <Field label="Doc reference" className="min-w-[180px] flex-1">
                <input className="field-input" value={docRef} onChange={(e) => setDocRef(e.target.value)} />
              </Field>
              <button
                className="btn-secondary"
                disabled={busy || !docRef}
                onClick={() => act(async () => {
                  const sha256 = await sha256Hex(docRef);
                  await api.post(`/api/v1/m20/permits/${id}/documents`, { docType, docRef, sha256 });
                  docsQ.reload();
                }, 'Document attached.')}
              >
                Attach
              </button>
            </div>
          </div>

          {['ACTIVE', 'REWORK_REQUIRED'].includes(p.state) ? (
            <div className="card p-5">
              <h2 className="mb-3 text-sm font-bold text-slate-700">Submit reinstatement evidence</h2>
              <Field label="Notes">
                <textarea className="field-input" rows={2} value={reinstatementNotes} onChange={(e) => setReinstatementNotes(e.target.value)} />
              </Field>
              <button
                className="btn-secondary mt-2"
                disabled={busy}
                onClick={() => act(async () => {
                  const photoDocRef = `reinstatement-${p.refNo}`;
                  const sha256 = await sha256Hex(photoDocRef + reinstatementNotes);
                  await api.post(`/api/v1/m20/permits/${id}/reinstatement/submit`, { photoDocRef, sha256, notes: reinstatementNotes || undefined });
                }, 'Reinstatement evidence submitted.')}
              >
                Submit for inspection
              </button>
            </div>
          ) : null}

          {isEngineer && p.state === 'REINSTATEMENT_REVIEW' ? (
            <div className="card p-5">
              <h2 className="mb-3 text-sm font-bold text-slate-700">Reinstatement inspection</h2>
              <Field label="Result">
                <select className="field-input" value={inspectResult} onChange={(e) => setInspectResult(e.target.value as typeof inspectResult)}>
                  <option value="PASS">Pass — release bond, close permit</option>
                  <option value="FAIL">Fail — rework required</option>
                </select>
              </Field>
              <button className="btn-primary mt-2" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m20/permits/${id}/reinstatement/inspect`, { result: inspectResult }), 'Reinstatement inspection recorded.')}>
                Record result
              </button>
            </div>
          ) : null}
        </div>

        <div className="space-y-5">
          <div className="card p-5">
            <h2 className="mb-2 text-sm font-bold text-slate-700">Reinstatement bond</h2>
            <p className="text-lg font-bold text-slate-900">{scr(p.reinstatementBondAmountScr)}</p>
            <p className="text-xs text-slate-500"><StatusBadge status={p.bondStatus} /></p>
          </div>

          <div className="card p-5">
            <h2 className="mb-3 text-sm font-bold text-slate-700">Actions</h2>
            {isEngineer && p.state === 'SUBMITTED' ? (
              <div className="mb-3 space-y-2">
                {p.hasClash ? (
                  <Field label="Clash override note (required)">
                    <input className="field-input" value={clashOverride} onChange={(e) => setClashOverride(e.target.value)} />
                  </Field>
                ) : null}
                <button
                  className="btn-success w-full"
                  disabled={busy || (p.hasClash && !clashOverride)}
                  onClick={() => act(() => api.post(`/api/v1/m20/permits/${id}/approve`, { clashOverrideNote: clashOverride || undefined }), 'Permit approved — bond intent raised.')}
                >
                  Approve
                </button>
                <input className="field-input" placeholder="Rejection reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
                <button className="btn-danger w-full" disabled={busy || !rejectReason} onClick={() => act(() => api.post(`/api/v1/m20/permits/${id}/reject`, { reason: rejectReason }), 'Permit rejected.')}>
                  Reject
                </button>
              </div>
            ) : null}
            {p.state === 'APPROVED' ? (
              <button className="btn-primary mb-3 w-full" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m20/permits/${id}/bond/confirm`), 'Bond confirmed — permit ACTIVE.')}>
                Confirm bond payment
              </button>
            ) : null}
            {isEngineer && ['APPROVED', 'ACTIVE'].includes(p.state) ? (
              <div className="space-y-2 border-t border-slate-100 pt-3">
                <input className="field-input" placeholder="Revocation reason" value={revokeReason} onChange={(e) => setRevokeReason(e.target.value)} />
                <button className="btn-danger w-full" disabled={busy || !revokeReason} onClick={() => act(() => api.post(`/api/v1/m20/permits/${id}/revoke`, { reason: revokeReason }), 'Permit revoked.')}>
                  Revoke
                </button>
              </div>
            ) : null}
            {!isEngineer && !['APPROVED'].includes(p.state) ? <p className="text-xs text-slate-400">Approval/rejection/revocation require ROADS_ENGINEER.</p> : null}
          </div>
        </div>
      </div>
    </div>
  );
}
